import { BookingCard } from 'components/business/search/booking-card/BookingCard';
import { ExclusivityEntry, ExclusivityMap, LockOptions } from 'types';
import { FC } from 'react';
import { LocalDateTime } from 'js-joda';
import { Stats } from 'utils/calendar';
import { consumer, core } from '@tock/coretypes';
import { find, findIndex, get, has } from 'es-toolkit/compat';
import { isPaymentCardHoldExperience } from 'utils/priceUtils';
import { timesForOfferedTimesAndDate } from 'utils/timeUtils';

// @VisibleForTesting
export interface CustomTimeData {
  value: string;
  dateTime: LocalDateTime;
  seatingAreaIds?: number[];
}

interface ExperienceTimesProps {
  availableTimes: Dictionary<Stats>;
  availableCommunalTicketCountsByTime: Dictionary<number>;
  experience: consumer.IOffering | undefined;
  isShipping: boolean;
  lockedDateTime?: string;
  lockedSeatingArea?: core.SeatingArea;
  lockedTimes: Dictionary<Stats>;
  lockRequestInProgress?: boolean;
  lockRequested?: string;
  maxCommunalAvailableCallout?: number;
  offeredTimes: Dictionary<Stats>;
  onSelect(dateTime: LocalDateTime, priceCents: number, options: LockOptions): void;
  rescheduleOffering?: consumer.Offering;
  selectedDate: string;
  selectedSize: number;
  showExclusive?: boolean;
  showWaitlist?: boolean;
  soldTimes: Dictionary<Stats>;
  specialExclusiveTimes?: ExclusivityMap;
  waitlistUrl?: string;
  seatingAreas?: core.IAvailabilitySeatingArea[];
}

// @VisibleForTesting
export const getAvailableSeatingAreas = (
  availableTimes: Dictionary<Stats>,
  timeObj: CustomTimeData,
  seatingAreas: core.IAvailabilitySeatingArea[]
) => {
  const foundAvailableTime = availableTimes[timeObj.value];
  const seatingAreaIdsForTime = foundAvailableTime?.seatingAreaIds || [];

  return seatingAreas
    .filter((area) => area?.id && area.name && seatingAreaIdsForTime.includes(area.id))
    .sort((a, b) => (a?.name || '').localeCompare(b?.name || ''));
};

export const ExperienceTimes: FC<ExperienceTimesProps> = ({
  availableTimes,
  availableCommunalTicketCountsByTime,
  experience,
  isShipping,
  lockedDateTime,
  lockedSeatingArea,
  lockedTimes,
  lockRequestInProgress,
  lockRequested,
  maxCommunalAvailableCallout = 12,
  offeredTimes,
  onSelect,
  rescheduleOffering,
  selectedDate,
  selectedSize,
  showExclusive,
  showWaitlist,
  soldTimes,
  specialExclusiveTimes,
  waitlistUrl,
  seatingAreas = [],
}) => {
  const renderTime = (timeObj: CustomTimeData, idx: number) => {
    const time = timeObj.value;
    const dateTime = timeObj.dateTime;
    const seatingAreasForTime: core.IAvailabilitySeatingArea[] = getAvailableSeatingAreas(
      availableTimes,
      timeObj,
      seatingAreas
    );

    const isAvailable = has(availableTimes, time);
    const communalTicketCount = get(availableCommunalTicketCountsByTime, time, 0);
    const isNotAvailableToCurrentUser = !isAvailable && has(lockedTimes, time);
    const isSoldOut = !isAvailable && !isNotAvailableToCurrentUser && has(soldTimes, time);
    const isNotOffered = !isAvailable && !isNotAvailableToCurrentUser && !isSoldOut;
    const exclusivityEntries = (specialExclusiveTimes || {})[time];

    const isRescheduling = !!rescheduleOffering;

    const reschedulePriceInfo: Stats | undefined = rescheduleOffering?.price?.pricePerPerson
      ?.maxCents
      ? {
          priceRange: {
            max: rescheduleOffering.price.pricePerPerson.maxCents, // same in the case of reschedule
            min: rescheduleOffering.price.pricePerPerson.maxCents,
          },
        }
      : undefined;

    const getPriceInfo = (): Stats => {
      if (isAvailable) {
        return availableTimes[time];
      }
      if (isNotAvailableToCurrentUser) {
        return lockedTimes[time];
      }
      if (isSoldOut) {
        return soldTimes[time];
      }

      return { priceRange: { min: 0, max: 0 } };
    };

    if (isNotOffered) {
      return null;
    }

    const priceInfo = getPriceInfo();
    const hidePrice = isPaymentCardHoldExperience(experience);

    const showNonExclusive =
      !exclusivityEntries || findIndex(exclusivityEntries, (entry) => entry.hasNonExclusive) !== -1;

    const renderResultComponent = (exclusiveEntry?: ExclusivityEntry) => (
      <BookingCard
        exclusivityEntry={exclusiveEntry ?? undefined}
        communalInfo={{
          ticketCount: communalTicketCount,
          maxCallout: maxCommunalAvailableCallout,
        }}
        experience={experience}
        isAvailable={isAvailable}
        isNotAvailableToCurrentUser={isNotAvailableToCurrentUser}
        isRescheduling={isRescheduling}
        isShipping={isShipping}
        isSoldOutTime={isSoldOut}
        key={time}
        userLockedDateTime={lockedDateTime}
        userLockedSeatingArea={lockedSeatingArea}
        onClick={onSelect}
        partySize={selectedSize}
        priceInfo={priceInfo}
        purchaseSize={selectedSize}
        reschedulePriceInfo={reschedulePriceInfo}
        showExclusive={showExclusive}
        showWaitlist={showWaitlist}
        time={dateTime.toString()}
        waitlistUrl={waitlistUrl}
        seatingAreas={seatingAreasForTime}
        soldOutSeatingAreaIds={isSoldOut ? soldTimes[time].seatingAreaIds : []}
        hidePrice={hidePrice}
      />
    );

    // show the exclusive version of this timeslot if showExclusive is true OR any of the exclusivity entries for this time slot are public (so will not display as exclusive but will be marked as exclusive when booked)
    return exclusivityEntries &&
      (showExclusive ||
        exclusivityEntries.findIndex((exclusiveEntry) => !exclusiveEntry.isExclusivityActive) !==
          -1)
      ? exclusivityEntries.map((exclusiveEntry) => renderResultComponent(exclusiveEntry))
      : showNonExclusive && renderResultComponent();
  };

  const times: CustomTimeData[] = timesForOfferedTimesAndDate(offeredTimes, selectedDate);

  if (isShipping) {
    const firstAvailable = find(times, (timeObj) => has(availableTimes, timeObj.value));
    if (firstAvailable) {
      return <div>{renderTime(firstAvailable, 0)}</div>;
    }
    const firstLocked = find(times, (timeObj) => has(lockedTimes, timeObj.value));
    if (firstLocked) {
      return <div>{renderTime(firstLocked, 0)}</div>;
    }
    const firstSold = find(times, (timeObj) => has(soldTimes, timeObj.value));
    if (firstSold) {
      return <div>{renderTime(firstSold, 0)}</div>;
    }
    return null;
  }

  if (times && times.length > 0) {
    return <div>{times.map(renderTime)}</div>;
  }

  return null;
};
